import React from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Modal from "../../../common/Modal";
import { toast } from "react-toastify";
import makeStyles from '@mui/styles/makeStyles';
import ConfirmModal from "../../../common/ConfirmModal";
import { API } from "aws-amplify";
import AddEditShift from "./AddEditShift";
import { isPersonnelImportV2Enabled } from "../../../shared/access";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "90%",
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    float: "right",
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
  completed: {
    color: theme.palette.success[600] + "!important",
  },
}));

const columns = [{ id: "name", label: "Shift Name", minWidth: 170 }];

function ShiftsIndex(props) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState({});
  const [shifts, setShifts] = React.useState([]);

  // EN-21290: Check feature flag
  const importV2Enabled = isPersonnelImportV2Enabled();

  // EN-21290: REQ #8 - Shift delete confirmation with personnel count
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteShiftData, setDeleteShiftData] = React.useState({ id: null, name: '', personnelCount: 0 });

  const loadShifts = () => {
    API.get("Core", "/api/v1/shift").then(
      (response) => {
        setShifts(response);
      },
      (error) => {
        toast("Could not get shifts: " + error.response.data.message, {
          position: "top-center",
          type: "error",
        });
      }
    );
  };

  React.useEffect(() => {
    loadShifts();
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleShiftAdd = () => {
    setOpen(false);
    loadShifts();
  };

  const handleEditClose = (shift) => {
    editOpen[shift.id] = false;
    setEditOpen(Object.assign({}, editOpen));
  };

  const handleEditOpen = (shift) => {
    editOpen[shift.id] = true;
    setEditOpen(Object.assign({}, editOpen));
  };

  const handleEditSuccess = (shift) => {
    editOpen[shift.id] = false;
    setEditOpen(Object.assign({}, editOpen));
    loadShifts();
  };

  // EN-21290: REQ #8 - Fetch personnel count before showing delete confirmation (only if V2 enabled)
  const handleDeleteClick = (shift) => {
    if (importV2Enabled) {
      // V2: Fetch usage count from new endpoint
      API.get("Core", `/api/v1/shift/${shift.id}/usage`).then(
        (response) => {
          const personnelCount = response.count || 0;
          setDeleteShiftData({
            id: shift.id,
            name: shift.name,
            personnelCount: personnelCount
          });
          setDeleteConfirmOpen(true);
        },
        (error) => {
          toast("Failed to check shift usage: " + (error.response?.data?.message || error.message), {
            position: "top-center",
            type: "error",
          });
        }
      );
    } else {
      // Legacy: Just show confirmation without count
      setDeleteShiftData({
        id: shift.id,
        name: shift.name,
        personnelCount: 0
      });
      setDeleteConfirmOpen(true);
    }
  };

  const deleteShift = () => {
    const shiftId = deleteShiftData.id;
    API.del("Core", "/api/v1/shift/" + shiftId).then(
      (response) => {
        const count = deleteShiftData.personnelCount;
        const message = count > 0
          ? `Shift deleted. ${count} personnel shift assignment${count > 1 ? 's' : ''} cleared.`
          : "Shift deleted successfully";

        toast(message, {
          position: "top-center",
          type: "success",
        });
        setDeleteConfirmOpen(false);
        loadShifts();
      },
      (error) => {
        toast("Could not Delete Shift: " + (error.response?.data?.message || error.message), {
          position: "top-center",
          type: "error",
        });
        setDeleteConfirmOpen(false);
      }
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Modal
          button={
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
            >
              Add Shift
            </Button>
          }
          content={
            <AddEditShift onCancel={handleClose} onSuccess={handleShiftAdd} />
          }
          title="Add Shift"
          size="small"
          handleClose={handleClose}
          handleOpen={handleOpen}
          open={open}
        />
      </Grid>
      <Grid xs={12}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((row) => {
              return (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                  {columns.map((column) => {
                    let value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number"
                          ? column.format(value)
                          : value}
                      </TableCell>
                    );
                  })}
                  <TableCell key={"edit"}>
                    <Modal
                      button={
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.button}
                        >
                          Edit
                        </Button>
                      }
                      content={
                        <AddEditShift
                          existingShift={row}
                          onCancel={handleEditClose.bind(this, row)}
                          onSuccess={handleEditSuccess.bind(this, row)}
                        />
                      }
                      title="Edit Shift"
                      size="small"
                      handleClose={handleEditClose.bind(this, row)}
                      handleOpen={handleEditOpen.bind(this, row)}
                      open={editOpen[row.id]}
                    />
                    {/* EN-21290: REQ #8 - Delete with personnel count */}
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      onClick={() => handleDeleteClick(row)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Grid>

      {/* EN-21290: REQ #8 - Delete confirmation with personnel count */}
      <ConfirmModal
        title={"Delete Shift"}
        text={
          deleteShiftData.personnelCount > 0 ? (
            <span>
              ⚠️ This shift is assigned to <b>{deleteShiftData.personnelCount}</b> personnel.
              Deleting will remove their shift assignment.<br/><br/>
              Are you sure you want to delete <b>{deleteShiftData.name}</b>?
            </span>
          ) : (
            <span>
              Are you sure you want to delete <b>{deleteShiftData.name}</b>?
            </span>
          )
        }
        onConfirm={deleteShift}
        onCancel={() => setDeleteConfirmOpen(false)}
        confirmTitle={"Delete"}
        open={deleteConfirmOpen}
      >
        {/* Empty - we're using it as a controlled modal */}
      </ConfirmModal>
    </Grid>
  );
}

export default ShiftsIndex;
